import alt from 'components/Dispatcher';
import TeamSource from 'sources/TeamSource';
import UserSource from 'sources/UserSource';
import generateAsyncActions from 'utils/generateAsyncActions';
import DepartmentStore from 'stores/department/DepartmentStore';
import DepartmentActions from 'actions/department/DepartmentActions';
import {List, Map} from 'immutable';
import {ActionsBase, DispatchAll, IncludeResultCallbacks} from '@store/@types/alt';
import Team from 'records/Team';
import {DepartmentId} from 'records/Department';
import User from 'records/User';
import {TeamId} from 'records/TeamId';

interface AsyncActions {
  fetchTeamsForDepartment(departmentId: DepartmentId): Promise<[DepartmentId, List<Team>]>;
  fetchTeamsForUser(): Promise<List<Team>>;
  createTeam(team: Team): Promise<Team>;
  updateTeam(team: Team): Promise<Team>;
  fetchUsers(teamIds: List<TeamId> | TeamId[]): Promise<List<User>>;
  fetchAllUsers(): Promise<List<User>>;
  deleteTeam(teamId: TeamId): Promise<void>;
  /**
   * For admins use call below
   * @deprecated - Loops through departments to query teams, must be a better way.
   */
  fetchAllTeams(isAdmin: boolean): Promise<List<Team>>;
  fetchAllTeamsForAdmin(): Promise<List<Team>>;

  /**
   * Includes inactive teams, used for metrics queries
   * @link resolveMetricTeamFilter
   */
  fetchTeamDepartments(): Promise<Map<TeamId, DepartmentId>>;
}

interface SyncActions {
  stageTeamForMove;
  stageDepartmentToReceive;
  discardTeamMove;
}

export interface TeamActionsInterface extends IncludeResultCallbacks<AsyncActions>, SyncActions {}

class TeamActions
  extends ActionsBase<AsyncActions, SyncActions>()
  implements DispatchAll<AsyncActions>
{
  constructor() {
    super();
    this.generateActions(
      'stageTeamForMove',
      'stageDepartmentToReceive',
      'discardTeamMove',
      ...generateAsyncActions<AsyncActions>(
        'fetchTeamsForDepartment',
        'fetchTeamsForUser',
        'createTeam',
        'updateTeam',
        'fetchUsers',
        'deleteTeam',
        'fetchAllTeams',
        'fetchAllTeamsForAdmin',
        'fetchTeamDepartments'
      )
    );
  }

  fetchTeamsForDepartment(departmentId) {
    return dispatch => {
      dispatch(departmentId);
      TeamSource.fetchTeamsForDepartment(departmentId).then(
        this.fetchTeamsForDepartmentSuccess,
        this.fetchTeamsForDepartmentError
      );
    };
  }

  fetchTeamsForUser() {
    return dispatch => {
      dispatch();
      TeamSource.fetchTeamsForUser().then(
        this.fetchTeamsForUserSuccess,
        this.fetchTeamsForUserError
      );
    };
  }

  fetchAllTeams(isAdmin) {
    return dispatch => {
      dispatch();
      const {departmentProfiles, areDepartmentProfilesLoading, areDepartmentProfilesLoaded} =
        DepartmentStore.getState();
      const departmentPromise =
        !areDepartmentProfilesLoading && !areDepartmentProfilesLoaded
          ? DepartmentActions.fetchDepartmentProfiles(isAdmin)
          : Promise.resolve(departmentProfiles);

      departmentPromise.then(profiles => {
        if (isAdmin) {
          profiles.forEach(({id}) => this.fetchTeamsForDepartment(id));
        } else {
          this.fetchTeamsForUser();
        }
      });
    };
  }

  commitTeamMove(team) {
    TeamSource.update(team).then(
      team => {
        this.updateTeamSuccess(team);
        this.discardTeamMove();
      },
      () => {
        this.updateTeamError();
        this.discardTeamMove();
      }
    );
  }
  // From a store i guess
  discardTeamMove() {}

  updateTeam(team) {
    return dispatch => {
      dispatch();
      return TeamSource.update(team).then(this.updateTeamSuccess, this.updateTeamError);
    };
  }

  createTeam(team) {
    return dispatch => {
      dispatch();
      return TeamSource.create(team).then(this.createTeamSuccess, this.createTeamError);
    };
  }

  fetchUsers(teamIds) {
    return dispatch => {
      dispatch();
      UserSource.fetchUsersInTeams(teamIds).then(this.fetchUsersSuccess, this.fetchUsersError);
    };
  }

  fetchAllUsers() {
    return dispatch => {
      dispatch();
      UserSource.fetchAll({}).then(this.fetchUsersSuccess, this.fetchUsersError);
    };
  }

  deleteTeam(teamId) {
    return dispatch => {
      dispatch();
      return TeamSource.delete(teamId).then(
        () => this.deleteTeamSuccess(teamId),
        this.deleteTeamError
      );
    };
  }

  fetchAllTeamsForAdmin() {
    return dispatch => {
      dispatch();
      return TeamSource.fetchAllTeamsForAdmin().then(
        this.fetchAllTeamsForAdminSuccess,
        this.fetchAllTeamsForAdminError
      );
    };
  }

  fetchTeamDepartments() {
    return dispatch => {
      dispatch();
      return TeamSource.fetchTeamDepartments().then(
        this.fetchTeamDepartmentsSuccess,
        this.fetchTeamDepartmentsError
      );
    };
  }
}

export default alt.createActions<AsyncActions, SyncActions>(TeamActions);
